// client/src/components/ViewerDashboard.js
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

function ViewerDashboard() {
  const [latestDeviceLocations, setLatestDeviceLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();

  // Define a default map center (e.g., Gurugram, India or a global default)
  const defaultMapCenter = {
    lat: 28.4595,
    lng: 77.0266,
  };
  const defaultMapZoom = 10;

  // Function to fetch device events for the viewer
  const fetchDeviceEvents = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const config = {
        headers: { "x-auth-token": token },
      };
      // Fetch all devices assigned to the current user (including viewer)
      const response = await axios.get(
        `${backendUrl}/users/my-devices`,
        config
      );
      const devices = response.data;

      const latestLocations = [];
      devices.forEach((device) => {
        // Prioritize liveData if available and valid
        if (
          device.liveData &&
          device.liveData.latitude !== undefined &&
          device.liveData.longitude !== undefined
        ) {
          latestLocations.push({
            deviceName: device.name,
            deviceId: device.deviceId,
            latitude: device.liveData.latitude,
            longitude: device.liveData.longitude,
            timestamp: device.liveData.timestamp || new Date(),
            eventType: "Live Data",
            speed: device.liveData.speed || 0,
            details: `Status: ${device.status || "N/A"}`,
          });
        }
        // Fallback to latest eventHistory if no liveData or liveData is incomplete
        else if (device.eventHistory && device.eventHistory.length > 0) {
          const latestEventWithLocation = device.eventHistory
            .filter(
              (event) =>
                event.latitude !== undefined && event.longitude !== undefined
            )
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

          if (latestEventWithLocation) {
            latestLocations.push({
              deviceName: device.name,
              deviceId: device.deviceId,
              ...latestEventWithLocation,
            });
          }
        }
      });

      // Sort by deviceName for consistent display, or by timestamp if preferred
      latestLocations.sort((a, b) => a.deviceName.localeCompare(b.deviceName));

      setLatestDeviceLocations(latestLocations);
      setLoading(false);
    } catch (err) {
      console.error(
        "Error fetching device events for viewer map:",
        err.response ? err.response.data : err.message
      );
      setError(
        err.response
          ? err.response.data.msg || "Failed to load device locations."
          : "Network error. Please try again."
      );
      setLoading(false);
    }
  }, [backendUrl, navigate]);

  useEffect(() => {
    fetchDeviceEvents();
  }, [fetchDeviceEvents]);

  // Determine the map center: use the first device's location if available, otherwise use default
  const mapCenter =
    latestDeviceLocations.length > 0
      ? {
          lat: latestDeviceLocations[0].latitude,
          lng: latestDeviceLocations[0].longitude,
        }
      : defaultMapCenter;

  // Determine the map zoom: use a slightly higher zoom if devices are present, otherwise default
  const mapZoom = latestDeviceLocations.length > 0 ? 12 : defaultMapZoom;

  if (loading) return <p className="message">Loading Viewer Dashboard...</p>;
  if (error) return <p className="message error">Error: {error}</p>;

  return (
    <div className="dashboard-card">
      <section className="dashboard-section map-section">
        <MapContainer
          center={[mapCenter.lat, mapCenter.lng]}
          zoom={mapZoom}
          className="leaf-map-responsive"
          
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {latestDeviceLocations.length > 0 ? (
            latestDeviceLocations.map((event) => (
              <Marker
                key={`viewer-device-${event.deviceId}`}
                position={[event.latitude, event.longitude]}
              >
                <Popup>
                  <b>Device:</b> {event.deviceName}
                  <br />
                  <b>Updated:</b> {new Date(event.timestamp).toLocaleString()}
                  <br />
                  <b>Type:</b> {event.eventType}
                  <br />
                  <b>Speed:</b> {event.speed || "N/A"}
                  <br />
                  {event.details && (
                    <span>
                      <b>Details:</b> {event.details}
                    </span>
                  )}
                </Popup>
              </Marker>
            ))
          ) : (
            // The placeholder is now a direct child of the map container, so its styling needs to be absolute
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                padding: "20px",
                borderRadius: "8px",
                textAlign: "center",
                zIndex: 1000,
                maxWidth: "80%",
                fontSize: "1.1rem",
              }}
            >
              No current location data available for your assigned devices.
              <br />
              Map showing a default location.
            </div>
          )}
        </MapContainer>
      </section>
    </div>
  );
}

export default ViewerDashboard;
